import { getSimpleISOString } from "@prismicio/editor-support/Date"
import {
  Banner,
  BannerButton,
  BannerDescription,
  BannerTitle,
} from "@prismicio/editor-ui"
import { type ReactNode, useLayoutEffect } from "react"
import { Link, type To } from "react-router-dom"

import { useCurrentUsage } from "../../settings/usage/useCurrentUsage"
import { env } from "../env"
import { getBaseRepositoryUrl, getRepositoryName } from "../routing"
import type { BillingPeriod } from "../services/billing"
import { useGlobalBanner } from "./GlobalBanner"

export function useUsageBanners() {
  const { showBanner } = useGlobalBanner()

  const { currentPeriod, usage } = useCurrentUsage()

  const {
    totalCloudFrontBandwidth,
    totalCloudFrontCalls,
    totalImgixBandwidth,
    totalImgixCalls,
  } = usage
  const totalBandwidth = bytesToGigabytes(
    totalCloudFrontBandwidth + totalImgixBandwidth,
  )
  const totalApiCalls = totalCloudFrontCalls + totalImgixCalls

  const repositoryId = getRepositoryName()
  const baseRepoUrl = getBaseRepositoryUrl(repositoryId)

  useLayoutEffect(() => {
    if (currentPeriod === undefined) return

    const {
      startDate,
      plan: {
        id: planId,
        quotas: { apiCalls: apiCallsQuota, bandwidth: bandwidthQuota },
        limits: { apiCalls: apiCallsLimit, bandwidth: bandwidthLimit },
      },
    } = currentPeriod

    const apiCallsBannerProps = getApiCallsBannerProps({
      apiCallsLimit,
      apiCallsQuota,
      baseRepoUrl,
      planId,
      startDate,
      totalApiCalls,
    })
    if (apiCallsBannerProps) {
      const { bannerId, title, description, linkTo, linkLabel } =
        apiCallsBannerProps
      showBanner({
        id: bannerId,
        render: ({ onClose }) => (
          <UsageBanner
            onClose={onClose}
            title={title}
            description={description}
            linkTo={linkTo}
            linkLabel={linkLabel}
          />
        ),
      })
    }

    const bandwidthBannerProps = getBandwidthBannerProps({
      bandwidthLimit,
      bandwidthQuota,
      baseRepoUrl,
      planId,
      startDate,
      totalBandwidth,
    })
    if (bandwidthBannerProps) {
      const { bannerId, title, description, linkTo, linkLabel } =
        bandwidthBannerProps
      showBanner({
        id: bannerId,
        render: ({ onClose }) => (
          <UsageBanner
            onClose={onClose}
            title={title}
            description={description}
            linkTo={linkTo}
            linkLabel={linkLabel}
          />
        ),
      })
    }
  }, [currentPeriod, totalApiCalls, totalBandwidth, baseRepoUrl, showBanner])
}

interface ApiCallsBannerData {
  apiCallsLimit: BillingPeriod["plan"]["limits"]["apiCalls"]
  apiCallsQuota: BillingPeriod["plan"]["quotas"]["apiCalls"]
  baseRepoUrl: string
  planId: BillingPeriod["plan"]["id"]
  startDate: BillingPeriod["startDate"]
  totalApiCalls: bigint
}

function getApiCallsBannerProps(data: ApiCallsBannerData) {
  const {
    apiCallsLimit,
    apiCallsQuota,
    baseRepoUrl,
    planId,
    startDate,
    totalApiCalls,
  } = data

  if (apiCallsLimit !== "unlimited" && totalApiCalls >= apiCallsLimit) {
    const bannerId = `${getSimpleISOString(startDate)}-exceeded-api-calls-limit-${planId}`
    if (env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId)) {
      return {
        bannerId,
        title: "API Call Limit Exceeded! Let's Talk Solutions.",
        description:
          "Congratulations on your continued growth - we can't wait to help you scale even further! Get in touch to explore the tailored solutions for our high-usage clients like you.",
        ...contactUsLinkProps,
      }
    } else if (planId === "personal") {
      return {
        bannerId,
        title: "API Call Limit Exceeded",
        description:
          "You have reached your current plan's limit. To avoid disruption, upgrade your plan.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    } else {
      return {
        bannerId,
        title: "API Call Limit Exceeded",
        description:
          "You have reached your current plan's limit. Your plan will be automatically upgraded to the next plan tier to support your growing usage.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    }
  } else if (
    apiCallsLimit !== "unlimited" &&
    totalApiCalls >= ninetyPercentWarningThreshold * apiCallsLimit
  ) {
    const bannerId = `${getSimpleISOString(startDate)}-approaching-90%-api-calls-limit-${planId}`
    if (env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId)) {
      return {
        bannerId,
        title: "API Call Quota Exceeded and Approaching Limit.",
        description:
          "You're at 90% of your current plan's limit. Get in touch to explore the tailored solutions for our high-usage clients like you.",
        ...contactUsLinkProps,
      }
    } else if (planId === "personal") {
      return {
        bannerId,
        title: "Approaching API Call Usage Limit",
        description:
          "You're at 90% of your current plan's limit. To avoid disruption, upgrade your plan.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    } else {
      return {
        bannerId,
        title: "API Call Quota Exceeded and Approaching Limit",
        description:
          "You're at 90% of your current plan's limit. Should you exceed this limit, your plan will be automatically upgraded to the next plan tier to support your growing usage.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    }
  } else if (
    apiCallsLimit !== "unlimited" &&
    totalApiCalls >= eightyPercentWarningThreshold * apiCallsLimit
  ) {
    const bannerId = `${getSimpleISOString(startDate)}-approaching-80%-api-calls-limit-${planId}`
    if (env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId)) {
      return {
        bannerId,
        title: "API Call Quota Exceeded and Approaching Limit.",
        description:
          "You're at 80% of your current plan's limit. Get in touch to explore the tailored solutions for our high-usage clients like you.",
        ...contactUsLinkProps,
      }
    } else if (planId === "personal") {
      return {
        bannerId,
        title: "Approaching API Call Usage Limit",
        description:
          "You're at 80% of your current plan's limit. To avoid disruption, upgrade your plan.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    } else {
      return {
        bannerId,
        title: "API Call Quota Exceeded and Approaching Limit",
        description:
          "You're at 80% of your current plan's limit. Should you exceed this limit, your plan will be automatically upgraded to the next plan tier to support your growing usage.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    }
  } else if (
    env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId) &&
    apiCallsQuota !== "unlimited" &&
    totalApiCalls >= apiCallsQuota
  ) {
    return {
      bannerId: `${getSimpleISOString(startDate)}-exceeded-api-calls-quota-${planId}`,
      title: "API Call Quota Exceeded: Overage Charges Apply",
      description:
        "Your repository has surpassed its current plan's quota. Overage charges now apply.",
      ...getCheckUsageLinkProps(baseRepoUrl),
    }
  } else if (
    env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId) &&
    apiCallsQuota !== "unlimited" &&
    totalApiCalls >= ninetyPercentWarningThreshold * apiCallsQuota
  ) {
    return {
      bannerId: `${getSimpleISOString(startDate)}-approaching-90%-api-calls-quota-${planId}`,
      title: "Approaching API Call Quota",
      description:
        "You're at 90% of your current plan's quota. Should you exceed this limit, overage charges will apply.",
      ...getCheckUsageLinkProps(baseRepoUrl),
    }
  } else if (
    env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId) &&
    apiCallsQuota !== "unlimited" &&
    totalApiCalls >= eightyPercentWarningThreshold * apiCallsQuota
  ) {
    return {
      bannerId: `${getSimpleISOString(startDate)}-approaching-80%-api-calls-quota-${planId}`,
      title: "Approaching API Call Quota",
      description:
        "You're at 80% of your current plan's quota. Should you exceed this limit, overage charges will apply.",
      ...getCheckUsageLinkProps(baseRepoUrl),
    }
  }

  return undefined
}

interface BandwidthBannerData {
  bandwidthLimit: BillingPeriod["plan"]["limits"]["bandwidth"]
  bandwidthQuota: BillingPeriod["plan"]["quotas"]["bandwidth"]
  baseRepoUrl: string
  planId: BillingPeriod["plan"]["id"]
  startDate: BillingPeriod["startDate"]
  totalBandwidth: bigint
}

function getBandwidthBannerProps(data: BandwidthBannerData) {
  const {
    bandwidthLimit,
    bandwidthQuota,
    baseRepoUrl,
    planId,
    startDate,
    totalBandwidth,
  } = data

  if (bandwidthLimit !== "unlimited" && totalBandwidth >= bandwidthLimit) {
    const bannerId = `${getSimpleISOString(startDate)}-exceeded-bandwidth-usage-limit-${planId}`
    if (env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId)) {
      return {
        bannerId,
        title: "CDN Bandwidth Limit Exceeded! Let's Talk Solutions.",
        description:
          "Congratulations on your continued growth - we can't wait to help you scale even further! Get in touch to explore the tailored solutions for our high-usage clients like you.",
        ...contactUsLinkProps,
      }
    } else if (planId === "personal") {
      return {
        bannerId,
        title: "CDN Bandwidth Limit Exceeded",
        description:
          "You have reached your current plan's limit. To avoid disruption, upgrade your plan.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    } else {
      return {
        bannerId,
        title: "CDN Bandwidth Limit Exceeded",
        description:
          "You have reached your current plan's limit. Your plan will be automatically upgraded to the next plan tier to support your growing usage.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    }
  } else if (
    bandwidthLimit !== "unlimited" &&
    totalBandwidth >= ninetyPercentWarningThreshold * bandwidthLimit
  ) {
    const bannerId = `${getSimpleISOString(startDate)}-approaching-90%-bandwidth-usage-limit-${planId}`
    if (env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId)) {
      return {
        bannerId,
        title: "CDN Bandwidth Quota Exceeded and Approaching Limit.",
        description:
          "You're at 90% of your current plan's limit. Get in touch to explore the tailored solutions for our high-usage clients like you.",
        ...contactUsLinkProps,
      }
    } else if (planId === "personal") {
      return {
        bannerId,
        title: "Approaching CDN Bandwidth Usage Limit",
        description:
          "You're at 90% of your current plan's limit. To avoid disruption, upgrade your plan.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    } else {
      return {
        bannerId,
        title: "CDN Bandwidth Quota Exceeded and Approaching Limit",
        description:
          "You're at 90% of your current plan's limit. Should you exceed this limit, your plan will be automatically upgraded to the next plan tier to support your growing usage.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    }
  } else if (
    bandwidthLimit !== "unlimited" &&
    totalBandwidth >= eightyPercentWarningThreshold * bandwidthLimit
  ) {
    const bannerId = `${getSimpleISOString(startDate)}-approaching-80%-bandwidth-usage-limit-${planId}`
    if (env.PRISMIC_STRIPE_PLATINUM_PRICE_IDS.includes(planId)) {
      return {
        bannerId,
        title: "CDN Bandwidth Quota Exceeded and Approaching Limit.",
        description:
          "You're at 80% of your current plan's limit. Get in touch to explore the tailored solutions for our high-usage clients like you.",
        ...contactUsLinkProps,
      }
    } else if (planId === "personal") {
      return {
        bannerId,
        title: "Approaching CDN Bandwidth Usage Limit",
        description:
          "You're at 80% of your current plan's limit. To avoid disruption, upgrade your plan.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    } else {
      return {
        bannerId,
        title: "CDN Bandwidth Quota Exceeded and Approaching Limit",
        description:
          "You're at 80% of your current plan's limit. Should you exceed this limit, your plan will be automatically upgraded to the next plan tier to support your growing usage.",
        ...getCheckUsageLinkProps(baseRepoUrl),
      }
    }
  } else if (
    planId !== "personal" &&
    bandwidthQuota !== "unlimited" &&
    totalBandwidth >= bandwidthQuota
  ) {
    return {
      bannerId: `${getSimpleISOString(startDate)}-exceeded-bandwidth-usage-quota-${planId}`,
      title: "CDN Bandwidth Quota Exceeded: Overage Charges Apply",
      description:
        "Your repository has surpassed its current plan's quota. Overage charges now apply.",
      ...getCheckUsageLinkProps(baseRepoUrl),
    }
  } else if (
    planId !== "personal" &&
    bandwidthQuota !== "unlimited" &&
    totalBandwidth >= ninetyPercentWarningThreshold * bandwidthQuota
  ) {
    return {
      bannerId: `${getSimpleISOString(startDate)}-approaching-90%-bandwidth-usage-quota-${planId}`,
      title: "Approaching CDN Bandwidth Quota",
      description:
        "You're at 90% of your current plan's quota. Should you exceed this limit, overage charges will apply.",
      ...getCheckUsageLinkProps(baseRepoUrl),
    }
  } else if (
    planId !== "personal" &&
    bandwidthQuota !== "unlimited" &&
    totalBandwidth >= eightyPercentWarningThreshold * bandwidthQuota
  ) {
    return {
      bannerId: `${getSimpleISOString(startDate)}-approaching-80%-bandwidth-usage-quota-${planId}`,
      title: "Approaching CDN Bandwidth Quota",
      description:
        "You're at 80% of your current plan's quota. Should you exceed this limit, overage charges will apply.",
      ...getCheckUsageLinkProps(baseRepoUrl),
    }
  }

  return undefined
}

const ninetyPercentWarningThreshold = 0.9 // 90%
const eightyPercentWarningThreshold = 0.8 // 80%

const contactUsLinkProps = {
  linkTo: "mailto:sales@prismic.io",
  linkLabel: "Contact us →",
}

function getCheckUsageLinkProps(baseRepoUrl: string) {
  return {
    linkTo: `${baseRepoUrl}/settings/usage`,
    linkLabel: "Check your usage →",
  }
}

interface UsageBannerProps {
  onClose: () => void
  title: ReactNode
  description: ReactNode
  linkTo: To
  linkLabel: ReactNode
}

function UsageBanner(props: UsageBannerProps) {
  const { onClose, title, description, linkTo, linkLabel } = props
  return (
    <Banner color="warn" iconName="alert" onClose={onClose}>
      <BannerTitle>{title}</BannerTitle>
      <BannerDescription>{description}</BannerDescription>
      <BannerButton asChild>
        <Link to={linkTo}>{linkLabel}</Link>
      </BannerButton>
    </Banner>
  )
}

function bytesToGigabytes(bytes: bigint): bigint {
  return bytes / 1_000_000_000n
}
